import { NGXLogger } from 'ngx-logger';
import { Component, ElementRef, HostBinding, HostListener, OnInit, OnDestroy, ViewChild, AfterViewInit, Input, inject, effect, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { NetworkStatusService } from 'src/app/services/network-status.service';
import { isOfflineCapableNative } from 'src/app/commons/utils/platform';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { ConfirmationService, MegaMenuItem, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { OutboxService } from 'src/app/offline/outbox.service';
import { PrimeNG } from 'primeng/config';
import { ToastMessagesService } from 'src/app/commons/services/toast-messages.service';
import { LogoutService } from 'src/app/services/logout.service';
import { SideBarMenuSharedService } from 'src/app/services/side-bar-menu-shared.service';
import { APP_VERSION } from 'src/environments/version';
import { CurrentUser } from 'src/app/interfaces/current-user';
import { UserService } from 'src/app/services/user.service';
import { UtilService } from 'src/app/services/util.service';
import { RoleMenuItem } from 'src/app/interfaces/role-menu-item';
import { AppConstants } from 'src/app/commons/app-constants';
import { SecretKeyService } from 'src/app/services/secret-key.service';
import { EMPTY, filter, switchMap } from 'rxjs';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { SideBarMenuService } from 'src/app/services/side-bar-menu.service';
import { PermissionService } from 'src/app/services/permission.service';
import { SHARED_MODULES } from '../../shared-imports';
import { TranslationService } from 'src/app/i18n/translation.service';
import { TranslatePipe } from 'src/app/i18n/translate.pipe';
import { TourService } from 'src/app/services/tour/tour.service';
import { CompanyMembershipService, CompanyMembership } from 'src/app/services/company-membership.service';
import { NotificationService } from 'src/app/notifications/notification.service';
import { HttpErrorResponse } from '@angular/common/http';
import { scheduleOrphanedOverlayMaskSweep } from 'src/app/commons/utils/overlay-mask-sweep';
import { normalizeMenuRouterLink } from 'src/app/commons/utils/menu-router-link';
import { FeedbackService } from 'src/app/services/feedback/feedback.service';
import { MenuStyleService, PhoneBars } from 'src/app/services/menu-style.service';
import { AiChatWidgetSharedService } from 'src/app/services/ai-chat-widget-shared.service';
import { LlIconComponent } from 'src/app/commons/icons/ll-icon.component';

@Component({
    selector: 'app-side-bar',
    templateUrl: './side-bar.component.html',
    styleUrls: ['./side-bar.component.css', './side-bar-dock.component.css'],
    imports: [SHARED_MODULES, TranslatePipe, LlIconComponent]
})
export class SideBarComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly logger = inject(NGXLogger);
  private readonly ngZone = inject(NgZone);
  private readonly feedbackService = inject(FeedbackService);
  /** How the menu is shown: classic drawer, bottom dock (phone) or side rail (tablet and wider). See MenuStyleService. */
  readonly menuStyle = inject(MenuStyleService);
  private readonly aiChatShared = inject(AiChatWidgetSharedService);

  sideBarWidth = '20rem';
  searchString: string = '';
  @Input() visibilityCondition: any;
  items!: MegaMenuItem[];
  // @Input() isMenuExpand= false;
  isMenuExpand!: boolean;
  user!: CurrentUser | null;
  imageIsValid: boolean = true; // Start with true by default
  ngAppVersion!: string;
  isSidebarMenuKeyTrigger = false;
  faChartLine = faChartLine;
  menuItems: RoleMenuItem[] = [];
  private rawMenuItems: RoleMenuItem[] = [];
  // Fixed bottom part of the drawer (Settings, Support, Sync Issues when there are some) and the "Recent" shortcuts.
  pinnedMenuItems: RoleMenuItem[] = [];
  recentItems: Array<{ link: string; label: string; icon: string }> = [];
  searching = false;
  private activeLink: string | null = null;
  private syncIssueCount = 0;
  private readonly outboxService = inject(OutboxService);
  //showSideBar = false;
  userProfile: any;
  // Object-URL for the offline-cached avatar blob (see SideBarMenuService.cacheAvatarBlob) —
  // revoked in ngOnDestroy.
  private offlineAvatarUrl: string | null = null;
  // See HeaderComponent's identical avatarLoadFailed/onAvatarImageError — same real bug (stale
  // signed URL + unreliable isOnline on Android), same fix, duplicated here since this drawer
  // keeps its own independent userProfile copy exactly like HeaderComponent does.
  avatarLoadFailed = false;
  onAvatarImageError(): void {
    this.avatarLoadFailed = true;
  }

  // Account menu (Edit Profile / Storage / Product Tour / Switch Company / Logout) — the
  // sidebar drawer's top identity card is the ONLY account-menu entry point on mobile (the
  // header's own avatar+menu is desktop-only), so it needs the full item set, not just the
  // read-only company-ID display it had before.
  accountMenuItems: MenuItem[] = [];
  memberships: CompanyMembership[] = [];
  multiCompanyEnabled = false;

  constructor(
    private primengConfig: PrimeNG,
    public router: Router,
    private sideBarMenuSharedService: SideBarMenuSharedService,
    private logOutService: LogoutService,
    private secretService: SecretKeyService,
    private toastMessageService: ToastMessagesService,
    private sideBarMenuService: SideBarMenuService,
    private translationService: TranslationService,
    private confirmationService: ConfirmationService,
    private permissionService: PermissionService,
    public networkStatusService: NetworkStatusService,
    private tourService: TourService,
    private companyMembershipService: CompanyMembershipService,
    private notificationService: NotificationService,
  ) {
    // buildAccountMenu() (which bakes in the switch-company/Edit-Profile offline-disable state)
    // was otherwise only ever rebuilt by unrelated triggers (profile load, permission change,
    // membership refresh) — never by connectivity itself changing. Same fix as HeaderComponent.
    effect(() => {
      this.networkStatusService.online();
      this.tourService.replayAllowed(); // rebuild when tours are switched on/off (Tour menu item)
      this.menuStyle.style(); // rebuild when the menu style / icon set rows change
      this.menuStyle.railDesktop(); // the "Hide the menu" row shows only while the side rail is the menu
      this.aiChatShared.enabled(); // "Ask Linc" row shows only while Linc is on
      this.buildAccountMenu();
    });
  }

  // Online, the live signed URL (or external OAuth avatar URL) renders directly. Offline, falls
  // back to whatever cacheAvatarBlob already cached, else undefined — the template's *ngIf
  // then falls through to the initials avatar instead of a broken-image icon.
  getAvatarImgSrc(): string | undefined {
    if (!this.avatarLoadFailed && (this.networkStatusService.isOnline || !isOfflineCapableNative())) {
      return this.userProfile?.profilePicture;
    }
    // Offline with no copy loaded yet → load it once (it may have been saved after start-up).
    if (!this.offlineAvatarUrl && !this.offlineAvatarLoading) this.refreshOfflineAvatar();
    return this.offlineAvatarUrl ?? undefined;
  }

  private offlineAvatarLoading = false;

  /** Re-reads the profile picture saved for offline use (see SideBarMenuService.avatarCached$). */
  private refreshOfflineAvatar(): void {
    this.offlineAvatarLoading = true;
    this.sideBarMenuService.getCachedAvatarUrl().then((url) => {
      if (url) {
        if (this.offlineAvatarUrl) URL.revokeObjectURL(this.offlineAvatarUrl);
        this.offlineAvatarUrl = url;
      }
    }).catch(() => undefined).finally(() => { this.offlineAvatarLoading = false; });
  }

  ngOnDestroy(): void {
    if (this.offlineAvatarUrl) {
      URL.revokeObjectURL(this.offlineAvatarUrl);
    }
    document.removeEventListener('keydown', this.ctrlMHandler, true);
  }

  showHideMenu() {
    this.sideBarMenuSharedService.isMenuExpanded.emit(true);
    this.focusSearchInput();
  }

  // Real bug confirmed live 2026-09-06: a brand-new user's very first dashboard visit (reached
  // via a fresh full-page load, e.g. straight after login) had this very first menu fetch come
  // back with zero items — "No menu items found" — despite the user genuinely holding real
  // granted permissions (confirmed directly in the database). Only ever seen on that first-ever
  // load; a later manual refresh was never actually tested but everything about the timing points
  // at a one-time race rather than a real permissions gap. One retry below self-heals that
  // without risk: a user who genuinely has zero menu items just sees the same correct empty
  // result again.
  // Up to 3 automatic retries with growing pauses (was ONE retry): a load that fails or comes back empty right
  // after login / signup (session or cache not settled yet) used to stay blank until the app was restarted.
  private menuRetryCount = 0;

  private loadMenuItems(forceRefresh: boolean = false) {
    this.sideBarMenuService.getSideBarMenu(forceRefresh).subscribe({
      next: (data: any) => {
        this.rawMenuItems = data ?? [];
        this.logger.debug('Sidebar menu loaded', {
          menuCount: this.rawMenuItems.length,
          labels: this.rawMenuItems.map((item: any) => item?.label)
        });
        if (this.rawMenuItems.length === 0) {
          this.logger.warn('Sidebar menu endpoint returned no items for current user.');
          this.retryOnceIfEmpty();
        } else {
          this.menuRetryCount = 0;
        }
        this.applyMenuTranslations();
        this.refreshSyncIssueCount();
        this.loadRecentItems();
        // console.log(`Menu Items ${JSON.stringify(this.accessibleMenuItems)}`);
        // this.updateAccessibleMenuItems();
      },
      error: (error: any) => {
        this.logger.error('Failed to load sidebar menu', error);
        this.rawMenuItems = [];
        this.applyMenuTranslations();
        this.retryOnceIfEmpty();
      }
    });
  }

  private retryOnceIfEmpty(): void {
    const pauses = [1500, 4000, 9000];
    if (this.menuRetryCount >= pauses.length) return;
    const pause = pauses[this.menuRetryCount++];
    // Goes through the shared refresh signal (not a direct loadMenuItems(true) call) so
    // HeaderComponent's own listener also re-fetches the profile its "Company ID:" row depends
    // on — the same first-load race plausibly explains both symptoms together.
    setTimeout(() => this.sideBarMenuService.requestMenuRefresh(), pause);
  }

  // Sync Issues lists offline changes the server refused — those only ever exist in the phone app, so the
  // website never shows the menu item (OFFLINE_PLAN.md 1.9, 2026-09-29). The server sends the same menu
  // to both, hence the filter here.
  private isSyncIssuesItem(i: any): boolean {
    return String(i?.routerLink ?? i?.link ?? '').replace(/^\//, '') === 'sync-issues';
  }

  // 2026-10-01: on the phone app the item shows only while there are refused changes to look at (count in brackets);
  // with none it is not in the way.
  private withoutPhoneOnlyItems(items: any[]): any[] {
    if (!Array.isArray(items)) return items;
    if (isOfflineCapableNative() && this.syncIssueCount > 0) return items;
    return items
      .filter((i) => !this.isSyncIssuesItem(i))
      .map((i) => (Array.isArray(i?.items) ? { ...i, items: this.withoutPhoneOnlyItems(i.items) } : i));
  }

  private applyMenuTranslations(): void {
    const translatedMenuItems = this.translateMenuItems(this.withoutPhoneOnlyItems(this.rawMenuItems));
    this.menuItems = translatedMenuItems;
    this.tempFilterMenuItems = translatedMenuItems;
    this.rebuildDisplay();
    this.buildAccountMenu(); // the company pop-up's shortcuts depend on the pages in this menu
    this.publishNewEntries();
    this.rebuildDockSlots();

    if (this.searchInput?.nativeElement?.value?.trim()) {
      this.filterMenuItems();
    }
  }

  // isRoot distinguishes top-level items (rendered by PanelMenu's own header
  // template, which reads `badge` correctly) from nested items (rendered by
  // PanelMenuSub, which has a confirmed upstream bug — primefaces/primeng#18930
  // — reading `processedItem.badge` directly instead of through the item-prop
  // resolver used for every other property in that same template, so it's
  // always undefined). Root items are left alone; nested items with a badge
  // get it baked into the label as HTML instead. PanelMenuSub's label element
  // is ALWAYS [innerHTML]-bound, but only reached when item.escape === false —
  // otherwise a separate, default branch renders the label as auto-escaped
  // plain text (confirmed against the actual template source), which is why
  // an earlier version of this fix showed the raw "<span>...</span>" markup
  // as literal text instead of rendering it.
  private translateMenuItems(items: RoleMenuItem[] = [], isRoot = true): RoleMenuItem[] {
    return items.map((item) => {
      let translatedLabel = item.label ? this.translationService.instant(item.label) : item.label;
      if (translatedLabel && this.syncIssueCount > 0 && this.isSyncIssuesItem(item)) {
        translatedLabel = `${translatedLabel} (${this.syncIssueCount})`;
      }
      const label = translatedLabel;
      return {
        ...item,
        label,
        escape: item.escape,
        // routerLink is intentionally removed: keeping it alongside command causes PrimeNG
        // PanelMenu to fire BOTH the routerLink directive AND the command on a single click,
        // resulting in two simultaneous Angular Router navigations that race each other and
        // can orphan a pending CanDeactivateGuard promise. The command callback below handles
        // all navigation, including the /new-tallysheet → /new-tallysheet/new redirect.
        routerLink: null,
        link: typeof item.routerLink === 'string' ? item.routerLink : undefined,
        command: item.routerLink ? () => this.navigateMenuItem(item.routerLink) : item.command,
        items: item.items ? this.translateMenuItems(item.items, false) : item.items,
      };
    });
  }

  private escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Drawer view: section headings, pinned part, active page, Recent ─────────────────────────────────────────────────────────
  // Pages whose own address is longer than the menu item's (details, edit screens) still light up the right item.
  private static readonly LINK_ALIASES: Array<[string, string]> = [
    ['/consignments', '/consignments/list'],
    // The dashboard address in the menu is /dashboard-v5; the app redirects it (and v3, v6, "new") to /dashboard-v7.
    ['/dashboard-v7', '/dashboard-v5'],
    ['/new-tallysheet', '/new-tallysheet/transport-unit/view'],
  ];

  private collectLinks(items: RoleMenuItem[], found = new Set<string>()): Set<string> {
    for (const item of items ?? []) {
      if (typeof item.link === 'string') found.add(item.link);
      if (item.items?.length) this.collectLinks(item.items, found);
    }
    return found;
  }

  /** The menu item that "owns" an address: the longest item address the page address starts with. */
  private bestLinkFor(url: string | null | undefined): string | null {
    if (!url) return null;
    const path = url.split('?')[0].split('#')[0];
    const links = this.collectLinks(this.menuItems);
    let best = '';
    for (const link of links) {
      if ((path === link || path.startsWith(link + '/')) && link.length > best.length) best = link;
    }
    if (!best) {
      for (const [prefix, target] of SideBarComponent.LINK_ALIASES) {
        if ((path === prefix || path.startsWith(prefix + '/')) && links.has(target)) best = target;
      }
    }
    return best || null;
  }

  private onRouteChanged(url: string): void {
    const best = this.bestLinkFor(url);
    this.activeLink = best;
    if (best && best !== '/dashboard-v5' && best !== '/sync-issues') this.recordRecent(best);
    if (this.menuItems.length && !this.searching) this.rebuildDisplay();
  }

  /** Marks the open page's item, opens only the group it is in (every other group closed). New objects: PrimeNG sees the change. */
  private markActive(items: RoleMenuItem[], best: string | null): RoleMenuItem[] {
    return items.map((item) => {
      const children = item.items ? this.markActive(item.items, best) : item.items;
      const isSelf = !!best && item.link === best;
      const hasActiveChild = !!children?.some((c) => c.link === best || c.expanded);
      return {
        ...item,
        items: children,
        expanded: hasActiveChild,
        styleClass: isSelf ? 'tms-sb-active' : undefined,
      };
    });
  }

  private sectionHeading(section: string): RoleMenuItem {
    return { label: this.translationService.instant(section), isHeading: true };
  }

  private withSectionHeadings(items: RoleMenuItem[]): RoleMenuItem[] {
    const out: RoleMenuItem[] = [];
    let last: string | null = null;
    for (const item of items) {
      const section = item.section ?? null;
      if (section && section !== last) {
        out.push(this.sectionHeading(section));
        last = section;
      }
      out.push(item);
    }
    return out;
  }

  /** Builds what the drawer shows in its normal (not searching) view. */
  private rebuildDisplay(): void {
    if (this.activeLink === null) this.activeLink = this.bestLinkFor(this.router.url);
    const marked = this.markActive(this.tempFilterMenuItems ?? [], this.activeLink);
    this.pinnedMenuItems = this.searching ? [] : marked.filter((i) => i.pinned);
    this.accessibleMenuItems = this.searching ? marked : this.withSectionHeadings(marked.filter((i) => !i.pinned));
  }

  // Recent: the last pages this person opened, newest first. Remembered on this device per person and company, and shown
  // only while the page is still in the menu the server built for them (so it can never lead anywhere they cannot go).
  private recentKey(): string | null {
    const u = this.userProfile;
    return u?.email ? `tms.sb.recent.v1.${u.email}.${u.companyId ?? ''}` : null;
  }

  private readRecent(): string[] {
    const key = this.recentKey();
    if (!key) return [];
    try {
      const parsed = JSON.parse(localStorage.getItem(key) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }

  private recordRecent(link: string): void {
    const key = this.recentKey();
    if (!key) return;
    try {
      const next = [link, ...this.readRecent().filter((x) => x !== link)].slice(0, 6);
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* storage blocked or full: Recent is a convenience only */
    }
  }

  private collectLeaves(items: RoleMenuItem[], found = new Map<string, RoleMenuItem>()): Map<string, RoleMenuItem> {
    for (const item of items ?? []) {
      if (item.items?.length) this.collectLeaves(item.items, found);
      else if (typeof item.routerLink === 'string') found.set(item.routerLink, item);
    }
    return found;
  }

  loadRecentItems(): void {
    const leaves = this.collectLeaves(this.withoutPhoneOnlyItems(this.rawMenuItems));
    this.recentItems = this.readRecent()
      .filter((l) => leaves.has(l) && l !== this.activeLink)
      .slice(0, 3)
      .map((l) => {
        const item = leaves.get(l)!;
        return { link: l, label: this.translationService.instant(item.label ?? ''), icon: item.icon || 'pi pi-clock' };
      });
  }

  openRecent(entry: { link: string }): void {
    this.navigateMenuItem(entry.link);
  }

  trackRecent(_: number, entry: { link: string }): string {
    return entry.link;
  }

  // Phone app only: how many offline changes the server refused (Sync Issues). Asked when the menu loads and each time the
  // drawer opens; the menu is rebuilt only when the number changed.
  private refreshSyncIssueCount(): void {
    if (!isOfflineCapableNative()) return;
    this.outboxService.getRejected().then((rows) => {
      const count = rows?.length ?? 0;
      if (count !== this.syncIssueCount) {
        this.syncIssueCount = count;
        this.applyMenuTranslations();
      }
    }).catch(() => { /* local database not ready: leave as is */ });
  }

  /** Drawer opened: refresh what depends on "now". */
  onShow(): void {
    this.activeLink = this.bestLinkFor(this.router.url);
    this.refreshSyncIssueCount();
    this.loadRecentItems();
    if (this.menuItems.length && !this.searching) this.rebuildDisplay();
  }

  // ── "+ New": one place to create things (the sidebar no longer has "Add ..." items) ─────────────────────────────────────
  // An entry is offered only when the user can use it: the action right (hasEffective(addCode)) AND the matching list is in
  // the menu the server built for this user (that already covers the view right, the plan and the modules).
  private readonly newEntries = [
    { addCode: 'SHIPMENT_ADD', listRoute: '/consignments/list', link: '/consignments', icon: 'pi pi-truck', labelKey: 'sidebar.newConsignment' },
    { addCode: 'TALLY_ADD', listRoute: '/new-tallysheet/transport-unit/view', link: '/new-tallysheet', icon: 'pi pi-box', labelKey: 'sidebar.newStockUnit' },
    { addCode: 'BP_ADD', listRoute: '/business-partners', link: '/business-partners/new', icon: 'pi pi-address-book', labelKey: 'sidebar.newBusinessPartner' },
    { addCode: 'PRODUCT_ADD', listRoute: '/products', link: '/add/product', icon: 'pi pi-tag', labelKey: 'sidebar.newProduct' },
    { addCode: 'LOADING_SITE_ADD', listRoute: '/site-locations', link: '/add/site-locations', icon: 'pi pi-map-marker', labelKey: 'sidebar.newLocation' },
  ];

  newMenuItems: MenuItem[] = [];

  private menuRoutes(items: RoleMenuItem[] = this.rawMenuItems, found = new Set<string>()): Set<string> {
    for (const item of items) {
      if (typeof item.routerLink === 'string') found.add(item.routerLink);
      if (item.items?.length) this.menuRoutes(item.items, found);
    }
    return found;
  }

  private availableNewEntries() {
    const routes = this.menuRoutes();
    return this.newEntries.filter((e) => routes.has(e.listRoute) && this.permissionService.hasEffective(e.addCode));
  }

  /** Hands the list to the header's "+ New" button (see SideBarMenuService.newEntries$). */
  private publishNewEntries(): void {
    this.sideBarMenuService.setNewEntries(this.availableNewEntries().map((e) => ({ labelKey: e.labelKey, icon: e.icon, link: e.link })));
    this.updateDockView();
  }

  canCreateAnything(): boolean {
    return this.availableNewEntries().length > 0;
  }

  openNewMenu(event: Event): void {
    this.newMenuItems = this.availableNewEntries().map((e) => ({
      label: this.translationService.instant(e.labelKey),
      icon: e.icon,
      command: () => this.navigateMenuItem(e.link),
    }));
    this.newMenu?.toggle(event);
  }

  private navigateMenuItem(routerLink: any): void {
    const normalizedLink = normalizeMenuRouterLink(routerLink);
    if (typeof normalizedLink === 'string') {
      // Close the drawer BEFORE kicking off navigation so its 320ms mask leave animation
      // gets a head start on the route-render work. When navigation resolves near-instantly
      // (offline — guards fast-fail without any network wait), rendering the new page in the
      // same frames can cancel the mask's CSS animation before animationend fires, and
      // PrimeNG then never removes the mask — leaving an invisible full-screen div at
      // z-index 10001 that swallows every tap. AppComponent additionally sweeps any mask
      // still stuck in leave state after navigation as the deterministic safety net.
      this.isMenuExpand = false;
      this.router.navigateByUrl(normalizedLink);
    }
  }

  filterMenuItems(term?: string) {
    const searchTerm = (term ?? this.searchInput.nativeElement.value).trim();
    this.logger.debug(`Search Term is ${searchTerm}`);

    // Back to the normal view when the box is empty or holds just one letter (one letter matches nearly every page).
    if (searchTerm.length < 2) {
      this.searching = false;
      this.rebuildDisplay(); // back to the normal view (headings, pinned part, all groups closed except the open page's)
      return;
    }

    // While searching: one flat list of matches (no headings, no pinned part).
    this.searching = true;
    this.pinnedMenuItems = [];
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    // Only what matches: a leaf that matches; a group with just its matching rows (all its rows when the group's own name
    // matches, e.g. "reports"). Order stays the menu order; every shown group is open.
    this.searchTermLower = lowerCaseSearchTerm;
    const matches = (label?: string) => !!label && label.toLowerCase().includes(lowerCaseSearchTerm);
    const results: RoleMenuItem[] = [];
    for (const item of this.tempFilterMenuItems) {
      if (item.items?.length) {
        const kids = matches(item.label) ? item.items : item.items.filter((c) => matches(c.label));
        if (kids.length) results.push({ ...item, items: kids, expanded: true, styleClass: undefined });
      } else if (matches(item.label)) {
        results.push(item);
      }
    }
    this.accessibleMenuItems = results;
  }


  private getUserProfile() {
    // With retry (2026-09-30) — see HeaderComponent.getUserProfile().
    this.sideBarMenuService.getUserProfileWithRetry().subscribe(
      (responseUserProfile: any) => {
        this.logger.debug(`In getUserProfile data ${JSON.stringify(responseUserProfile)}`);
        this.userProfile = responseUserProfile;
        this.buildAccountMenu();
        this.avatarLoadFailed = false;
        this.sideBarMenuService.setEncryptedUserProfile(responseUserProfile).then(() => {
          //  console.log('User profile encrypted and set successfully');
        }).catch((error: any) => {
          this.logger.error('Failed to set encrypted user profile:', error);
        });
      },
      (error: any) => { },
      () => { });

  }

  ngAfterViewInit(): void {
    this.preloadImage();

  }

  ngOnInit(): void {
    document.addEventListener('keydown', this.ctrlMHandler, true);
    this.urlListener();
    // Seed from the shared encrypted localStorage cache (same one HeaderComponent writes/reads
    // via sideBarMenuService.setEncryptedUserProfile/getDecryptedUserProfile) before attempting
    // the live fetch. getUserProfile()'s error handler is silent, so without this seed step,
    // going offline before this call resolves leaves userProfile permanently undefined — the
    // "Hello, !" / "Company ID: —" / avatar "U" bug.
    this.sideBarMenuService.getDecryptedUserProfile().then((profile) => {
      if (profile) {
        this.userProfile = profile;
        this.buildAccountMenu();
      }
      this.getUserProfile();
    });
    // Re-read once the picture is saved (2026-09-29) — at start-up it often isn't saved yet.
    this.refreshOfflineAvatar();
    this.sideBarMenuService.avatarCached$.subscribe(() => this.refreshOfflineAvatar());
    this.loadMenuItems();
    this.permissionService.loadMyPermissions().subscribe();
    this.permissionService.permissions$.subscribe(() => this.publishNewEntries());
    this.sideBarMenuService.refreshMenu$.subscribe(() => {
      this.getUserProfile();
      this.loadMenuItems(true);
      this.permissionService.refreshPermissions().subscribe();
    });
    this.sideBarMenuService.avatarUpdated$.subscribe((profilePicture) => {
      if (this.userProfile) {
        this.userProfile = { ...this.userProfile, profilePicture };
        this.avatarLoadFailed = false;
        this.sideBarMenuService.setEncryptedUserProfile(this.userProfile);
      }
    });
    // See HeaderComponent's identical resume listener — the signed avatar URL expires after 4
    // hours; refetch on every app resume so a long-backgrounded session doesn't sit on a stale
    // URL until a full restart.
    if (isOfflineCapableNative()) {
      App.addListener('resume', () => {
        this.ngZone.run(() => this.getUserProfile());
      });
    }
    this.translationService.language$.subscribe(() => {
      this.applyMenuTranslations();
      this.buildAccountMenu();
    });
    this.buildAccountMenu();
    this.loadMemberships();
    // Same reasoning as HeaderComponent — memberships were otherwise only ever loaded once, at
    // bootstrap, so accepting an invite in-app never refreshed the sidebar's switcher either.
    this.notificationService.refresh$.subscribe(() => this.loadMemberships());

    // this.testMenu();
    this.ngAppVersion = APP_VERSION;

    // console.log(`ngOnInit `);
    this.primengConfig.ripple.set(true);

    this.sideBarMenuSharedService.isMenuExpanded
      .pipe(
        switchMap((data: boolean) => {
          this.isMenuExpand = data;
          const enc_user = localStorage.getItem(AppConstants.USER) || '';
          const password = localStorage.getItem(AppConstants.PROMOTOER_CODE);
          if (!enc_user || !password) {
            // handle no user or password in local storage case
            // This could be throwing an error or returning a default value
            // return throwError('No user or key found in local storage');
            // return of({} as User); // assuming `User` is the type of `this.user`
            return EMPTY; // using `EMPTY` to complete the observable without emitting any value
          }
          return this.secretService.decrypt(enc_user, password);
        })
      )
      .subscribe(
        (decryptedUser: any) => {
          this.user = JSON.parse(decryptedUser ?? '');
          //console.log(`£££££££ ${JSON.stringify(this.user)}`);
          //  this.updateAccessibleMenuItems();
        },
        (error: any) => {
          this.logger.error('Failed to decrypt user data:', error);
        },
        () => {
          this.logger.debug('ONCOMPLETE FOCUS');
          this.focusSearchInput();
        }
      );

  }

  visibleSidebar1(flag: boolean) {
    //  console.log(`Flag ${flag}`);
  }

  onLogout() {
    this.confirmationService.confirm({
      key: 'sidebarLogoutConfirm',
      message: 'Are you sure you want to logout?',
      header: 'Confirm Logout',
      icon: 'pi pi-power-off',
      accept: () => {
        this.logOutService.logout().subscribe((isSuccess) => {
          this.logger.debug("Sidebar logout result", { isSuccess });
          if (isSuccess) {
            this.toastMessageService.showSuccessMessage(
              "Logout Successful",
              "You have been successfully logged out.",
              false,
              "logout-toast",
            );
          } else {
            this.toastMessageService.showErrorMessage(
              "Logout failed",
              "Error while log out",
            );
          }
        });
      }
    });
  }

  // Builds the drawer's top-card account menu (Edit Profile / Storage / Product Tour /
  // Switch Company / Logout) — the same item set as HeaderComponent's avatar menu, since
  // this card is the only account-menu entry point reachable on mobile.
  // Two pop-ups, two identities (2026-10-01): the ACCOUNT pop-up (the person, bottom card) and the COMPANY pop-up (top chip).
  workspaceMenuItems: MenuItem[] = [];
  idCopied = false;
  private idCopiedTimer: ReturnType<typeof setTimeout> | undefined;

  /** One or two letters for the company badge. */
  get companyInitials(): string {
    const name = (this.activeCompanyDisplayName || this.userProfile?.companyName || '').trim();
    if (!name) return 'L';
    const words = name.split(/\s+/).filter(Boolean);
    return ((words[0]?.[0] ?? '') + (words.length > 1 ? words[1][0] : '')).toUpperCase();
  }

  private buildAccountMenu(): void {
    const online = this.networkStatusService.isOnline;
    // edit/account-details and storage are OfflineGuard-protected: disabled + label suffix offline (same treatment as before).
    const unavailable = !online ? ` (${this.translationService.instant('offline.unavailableTitle')})` : '';
    this.accountMenuItems = [
      {
        label: this.translationService.instant('menu.editProfile') + unavailable,
        icon: 'pi pi-fw pi-pencil',
        disabled: !online,
        command: () => this.router.navigateByUrl('edit/account-details'),
      },
      {
        label: this.translationService.instant('menu.storage') + unavailable,
        icon: 'pi pi-fw pi-database',
        disabled: !online,
        command: () => this.router.navigateByUrl('storage'),
      },
      {
        label: this.translationService.instant('layout.productTour'),
        icon: 'pi pi-fw pi-compass',
        visible: this.tourService.replayAllowed(),
        command: () => this.tourService.startTour(true),
      },
      {
        // the round Linc / Support button is hidden (2026-10-07): Linc AI help opens from here
        label: this.translationService.instant('menuDock.askLinc'),
        icon: 'pi pi-fw pi-comment',
        visible: this.aiChatShared.enabled(),
        command: () => { this.isMenuExpand = false; this.aiChatShared.requestOpen(); },
      },
      {
        label: this.translationService.instant('appFeedback.menu'),
        icon: 'pi pi-fw pi-star',
        command: () => this.feedbackService.openDialog(),
      },
      { separator: true },
      {
        label: this.translationService.instant(this.menuStyle.dockOn() ? 'menuDock.useClassic' : 'menuDock.useDock'),
        icon: 'pi pi-fw pi-window-maximize',
        command: () => this.menuStyle.toggleStyle(),
      },
      ...(this.menuStyle.railDesktop()
        ? [{
            label: this.translationService.instant('menuDock.railHide'),
            icon: 'pi pi-fw pi-angle-double-left',
            command: () => this.hideRail(),
          }]
        : []),
      { separator: true },
      {
        label: this.translationService.instant('common.logout'),
        icon: 'pi pi-power-off',
        command: () => this.onLogout(),
      },
    ];
    this.buildWorkspaceMenu();
  }

  // The company pop-up: switch company, leave a company that is not the person's own, and shortcuts to the company pages this
  // person may open (only pages that are in the menu the server built for them). The company ID chip sits in its header.
  private buildWorkspaceMenu(): void {
    const activeMembership = this.memberships.find((m) => m.isActive);
    const canLeaveActiveCompany = !!activeMembership && !activeMembership.isDefault;
    const routes = this.menuRoutes();
    const shortcuts: MenuItem[] = [
      { route: '/company', label: this.translationService.instant('menu.companyProfile'), icon: 'pi pi-fw pi-id-card' },
      { route: '/company/branding', label: this.translationService.instant('menu.companyBranding'), icon: 'pi pi-fw pi-image' },
      { route: '/subscriptions', label: this.translationService.instant('menu.subscription'), icon: 'pi pi-fw pi-credit-card' },
    ]
      .filter((s) => routes.has(s.route))
      .map((s) => ({ label: s.label, icon: s.icon, command: () => this.navigateMenuItem(s.route) }));
    const items: MenuItem[] = [
      ...this.companyMembershipService.buildSwitchCompanyMenuItems(
        this.memberships,
        this.translationService.instant('common.switchCompany'),
        (companyId) => this.switchToCompany(companyId),
      ),
    ];
    if (shortcuts.length) {
      if (items.length) items.push({ separator: true });
      items.push(...shortcuts);
    }
    if (canLeaveActiveCompany) {
      items.push({ separator: true });
      items.push({
        label: this.translationService.instant('common.leaveCompany'),
        icon: 'pi pi-sign-out',
        command: () => this.leaveActiveCompany(activeMembership!.companyId),
      });
    }
    this.workspaceMenuItems = items;
  }

  // Real gap found 2026-07-27: the active company's name was only ever visible after
  // clicking the identity card's chevron (Switch Company list) — nowhere persistent in the
  // drawer itself, despite this being exactly the context a multi-company user most needs a
  // constant reminder of. Reuses the same getDisambiguatedName() the switcher itself uses,
  // so a company sharing its display name with another one in the list still reads
  // unambiguously (e.g. "RIK Exim Private Limited - 1") rather than looking identical to it.
  get activeCompanyDisplayName(): string {
    const active = this.memberships.find(m => m.isActive);
    return active ? this.companyMembershipService.getDisambiguatedName(this.memberships, active) : '';
  }

  private loadMemberships(): void {
    this.companyMembershipService.getMyMemberships().subscribe({
      next: (res) => {
        this.multiCompanyEnabled = res.enabled;
        this.memberships = res.memberships || [];
        this.buildAccountMenu();
      },
      error: () => {
        this.multiCompanyEnabled = false;
        this.memberships = [];
        this.buildAccountMenu();
      },
    });
  }

  async switchToCompany(companyId: number): Promise<void> {
    const target = this.memberships.find(m => m.companyId === companyId);
    if (!target || target.isActive) {
      return; // already active (or unknown) — no-op
    }
    // Mirrors HeaderComponent.switchToCompany — checked at action time, not menu-build time.
    // See CompanyMembershipService.hasUnsyncedWork for why switching with queued writes is unsafe.
    if (await this.companyMembershipService.hasUnsyncedWork()) {
      this.toastMessageService.showErrorMessage(
        this.translationService.instant('common.switchCompany'),
        this.translationService.instant('common.switchCompanyBlockedUnsynced'),
      );
      return;
    }
    const message = this.translationService
      .instant('common.switchCompanyConfirmMessage')
      .replace('{{companyName}}', this.companyMembershipService.getDisambiguatedName(this.memberships, target));
    this.confirmationService.confirm({
      key: 'sidebarSwitchCompanyConfirm',
      message,
      header: this.translationService.instant('common.confirmSwitchCompany'),
      icon: 'pi pi-building',
      accept: () => this.performCompanySwitch(companyId),
    });
  }

  leaveActiveCompany(companyId: number): void {
    const target = this.memberships.find(m => m.companyId === companyId);
    const displayName = target ? this.companyMembershipService.getDisambiguatedName(this.memberships, target) : '';
    this.confirmationService.confirm({
      key: 'sidebarSwitchCompanyConfirm',
      message: this.translationService.instant('common.leaveCompanyConfirmMessage').replace('{{companyName}}', displayName),
      header: this.translationService.instant('common.leaveCompany'),
      icon: 'pi pi-sign-out',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.performLeaveCompany(companyId),
    });
  }

  private performLeaveCompany(companyId: number): void {
    this.companyMembershipService.leaveCompany(companyId).subscribe({
      next: () => {
        window.location.href = '/';
      },
      error: (error: HttpErrorResponse) => {
        this.toastMessageService.showErrorMessage(
          this.translationService.instant('common.error'),
          error?.error?.message || this.translationService.instant('common.somethingWentWrong'),
        );
      },
    });
  }

  private performCompanySwitch(companyId: number): void {
    this.companyMembershipService.switchCompany(companyId).subscribe({
      next: () => {
        window.location.href = '/';
      },
      error: (error: HttpErrorResponse) => {
        this.toastMessageService.showErrorMessage(
          this.translationService.instant('common.error'),
          error?.error?.error || this.translationService.instant('common.somethingWentWrong'),
        );
      },
    });
  }

  isExpand = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // console.log(`is mobile ${this.utilService.isMobile()}`);
  }
  accessibleMenuItems!: RoleMenuItem[];
  tempFilterMenuItems!: RoleMenuItem[];
  urlListener() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event instanceof NavigationEnd) {
          //  this.setMenuExpandedState(event.urlAfterRedirects);
          this.onRouteChanged(event.urlAfterRedirects);
        }
      });
  }

  private setMenuExpandedState(url: string) {
    // Reset all expanded and selected properties
    this.menuItems.forEach((item) => {
      item.expanded = false;
      item.selected = false;
      item.items?.forEach((subItem) => {
        subItem.selected = false;
      });
    });

    this.logger.debug('URL:', url); // Log the current URL

    // Check the current URL and expand the corresponding menu item
    this.menuItems.forEach((menuItem) => {
      menuItem.items?.forEach((subItem) => {
        const coreLabel = subItem?.label
          ?.toLowerCase()
          .replace('add ', '')
          .replace('view ', '');
        if (url.includes(coreLabel ?? '')) {
          menuItem.expanded = true;
          subItem.styleClass = 'selected'; // apply the 'selected' class to subItem
          this.logger.debug(
            `Found match: Expanded '${menuItem.label}', selected '${subItem.label}'`
          ); // Log the expanded and selected items
        }
      });
    });

    // Log the final state of the menuItems for debugging
    this.logger.debug('Final state of menuItems:', this.menuItems);
  }



  clearFilter() {
    // Reset the searchString
    this.searchString = '';

    // Back to the normal view
    this.searching = false;
    this.rebuildDisplay();
  }

  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('newMenu') private newMenu?: Menu;
  @ViewChild('navRef', { read: ElementRef }) navElRef?: ElementRef<HTMLElement>;
  @ViewChild('pinnedMenu') private pinnedMenu?: Menu;

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // metaKey is for Mac, ctrlKey is for Windows — this used to only check metaKey despite this
    // exact comment saying otherwise, so Ctrl+Shift+M silently did nothing on Windows. Fixed
    // 2026-08-25.
    if (
      (event.metaKey || event.ctrlKey) &&
      event.shiftKey &&
      event.key.toLowerCase() === AppConstants.SHOW_MENU_HOT_KEY
    ) {
      // this.isMenuExpand = true;
      this.openMenuFromShortcut();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleShortcut3(event: KeyboardEvent) {
    if (
      (event.altKey && event.code === 'KeyM') ||
      (event.code === 'AltLeft' && event.key.toLowerCase() === AppConstants.SHOW_MENU_HOT_KEY) ||
      (event.code === 'AltRight' && event.key.toLowerCase() === AppConstants.SHOW_MENU_HOT_KEY)
    ) {
      this.openMenuFromShortcut();
    }
  }

  // Plain Ctrl+M (Cmd+M on Mac), additive alongside handleKeyboardEvent (Ctrl/Cmd+Shift+M)
  // and handleShortcut3 (Alt+M) above — deliberately not merged into either, so those two
  // stay untouched. Registered in the capture phase (see ngOnInit/ngOnDestroy) rather than as
  // an @HostListener: AG-Grid calls stopPropagation() on keydown while a tally cell is
  // focused/editing, which silently swallows bubble-phase window/document:keydown listeners
  // (confirmed pattern — see the capture-phase Ctrl+S handler in tallysheet-list-round/
  // tallysheet-square-list). Capture phase runs before AG-Grid's own handler can block it, so
  // this one shortcut stays reliable everywhere the other two currently are not guaranteed to be.
  private readonly ctrlMHandler = (event: KeyboardEvent) => {
    if (
      !((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey &&
        event.key.toLowerCase() === AppConstants.SHOW_MENU_HOT_KEY)
    ) {
      return;
    }
    event.preventDefault();
    this.openMenuFromShortcut();
  };

  /** Ctrl+M, Ctrl+Shift+M, Alt+M: with the side rail the rail's own search box opens; otherwise the menu opens as before. */
  private openMenuFromShortcut(): void {
    this.isSidebarMenuKeyTrigger = true;
    if (this.menuStyle.railDesktop()) {
      this.openRailSearch();
      return;
    }
    this.sideBarMenuSharedService.isMenuExpanded.emit(true);
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    }, 200);
  }

  // ── Docked menu (2026-10-07): bottom dock on phones, side rail on tablets and desktops ──────────────────────────────────
  // Both read the SAME menu the server built for this person (rights, plan, modules); nothing here decides what a person may see.
  dockSlots: Array<{ link: string; label: string; icon: string }> = [];
  dockView: { left: Array<{ link: string; label: string; icon: string }>; right: Array<{ link: string; label: string; icon: string }>; create: boolean } = { left: [], right: [], create: false };
  // First found (in this order) among the pages the person has: their own daily pages. Everything else is behind "More".
  private static readonly DOCK_CANDIDATES = [
    '/inventory/my-tasks', '/dashboard-v5', '/consignments/list', '/new-tallysheet/transport-unit/view',
    '/inventory/processing', '/inventory/overview', '/command-center', '/crm', '/affiliate',
  ];

  private rebuildDockSlots(): void {
    const leaves = this.collectLeaves(this.withoutPhoneOnlyItems(this.rawMenuItems));
    const slots: Array<{ link: string; label: string; icon: string }> = [];
    for (const link of SideBarComponent.DOCK_CANDIDATES) {
      const item = leaves.get(link);
      if (!item) continue;
      slots.push({ link, label: this.translationService.instant(item.label ?? ''), icon: item.icon || 'pi pi-circle' });
    }
    this.dockSlots = slots.slice(0, 4);
    this.updateDockView();
  }

  /** Three pages + the "+ New" button in the middle when this person can create things; four pages otherwise. */
  private updateDockView(): void {
    const create = this.canCreateAnything();
    const all = this.dockSlots.slice(0, create ? 3 : 4);
    this.dockView = create ? { left: all.slice(0, 2), right: all.slice(2), create } : { left: all, right: [], create };
  }

  isDockActive(slot: { link: string }): boolean {
    if (!this.activeLink) return false;
    return this.activeLink === slot.link || (slot.link === '/inventory/overview' && this.activeLink.startsWith('/inventory/'));
  }

  openDockSlot(slot: { link: string }): void {
    this.navigateMenuItem(slot.link);
  }

  /** The dock's "More": the same menu as the old drawer, as a sheet rising from the bottom. */
  openMore(): void {
    this.isMenuExpand = true;
  }

  /** "Hide bottom bar" / "Focus mode" from the More sheet. The thin strip at the bottom brings the bars back. */
  hideBars(mode: PhoneBars): void {
    this.menuStyle.setBars(mode);
    this.isMenuExpand = false;
  }

  cycleIconSet(): void {
    const order = ['lucide', 'phosphor', 'classic'] as const;
    const next = order[(order.indexOf(this.menuStyle.iconSet()) + 1) % order.length];
    this.menuStyle.setIconSet(next);
  }

  // Side rail (tablet and wider): narrow icons that open over the page on hover, or pinned open beside it, or hidden except a small tab.
  /** The component's own box in the page: 64px (rail), 268px (rail pinned), 0 (rail hidden); nothing in the classic or phone layouts. */
  @HostBinding('class.tms-rail-host') get hostRail(): boolean { return this.menuStyle.railDesktop(); }
  @HostBinding('class.rail-pinned') get hostRailPinned(): boolean { return this.menuStyle.railDesktop() && this.menuStyle.railMode() === 'pinned'; }
  @HostBinding('class.rail-hidden') get hostRailHidden(): boolean { return this.menuStyle.railDesktop() && this.menuStyle.railMode() === 'hidden'; }

  railHover = false;
  railSearchOpen = false;
  @ViewChild('railSearch') private railSearchInput?: ElementRef<HTMLInputElement>;

  get railPinned(): boolean {
    return this.menuStyle.railMode() === 'pinned';
  }

  get railWide(): boolean {
    return this.menuStyle.railMode() === 'pinned' || this.railHover || this.railSearchOpen;
  }

  onRailEnter(): void {
    if (this.menuStyle.railMode() === 'collapsed') this.railHover = true;
  }

  onRailLeave(): void {
    this.railHover = false;
  }

  /** The floating (not pinned) menu closes when you click anywhere outside it, or press Escape. A pinned menu is never touched. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.railHover && !this.railSearchOpen) return;
    const target = event.target as HTMLElement | null;
    if (target && target.closest && target.closest('.tms-rail')) return;
    this.railHover = false;
    this.railSearchOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.railHover || this.railSearchOpen) {
      this.railHover = false;
      this.railSearchOpen = false;
    }
  }

  toggleRailPin(): void {
    this.menuStyle.setRailMode(this.menuStyle.railMode() === 'pinned' ? 'collapsed' : 'pinned');
    this.railHover = false;
  }

  hideRail(): void {
    this.menuStyle.setRailMode('hidden');
    this.railHover = false;
    this.railSearchOpen = false;
  }

  showRail(): void {
    this.menuStyle.setRailMode('collapsed');
  }

  openRailSearch(): void {
    if (this.menuStyle.railMode() === 'hidden') this.menuStyle.setRailMode('collapsed');
    this.railSearchOpen = true;
    setTimeout(() => this.railSearchInput?.nativeElement.focus(), 60);
  }

  onRailSearchInput(value: string): void {
    this.searchString = value;
    this.filterMenuItems(value);
  }

  onRailSearchBlur(): void {
    if (!this.searchString.trim()) this.railSearchOpen = false;
  }

  railGroupActive(item: RoleMenuItem): boolean {
    return !!this.activeLink && !!item.items?.some((c) => c.link === this.activeLink);
  }

  /** A group opens inline while the rail is wide, as a pop-up list beside it while narrow; a page opens directly. */
  onRailRowClick(event: Event, item: RoleMenuItem): void {
    if (item.items?.length) {
      if (this.railWide) this.toggleNavGroup(item);
      else this.openPinned(event, item);
      return;
    }
    if (item.link) {
      this.navigateMenuItem(item.link);
      this.railHover = false;
      this.railSearchOpen = false;
      if (this.searchString) this.clearFilter();
    }
  }

  /** A long name is never cut with "…": the first words stay normal, the rest drops to a smaller second line. */
  tierParts(label: string | undefined, singleMax = 22, mainMax = 14): { main: string; sub: string } {
    const text = (label ?? '').trim();
    if (text.length <= singleMax) return { main: text, sub: '' };
    const words = text.split(/\s+/);
    let main = '';
    let i = 0;
    while (i < words.length && (main + ' ' + words[i]).trim().length <= mainMax) {
      main = (main + ' ' + words[i]).trim();
      i++;
    }
    if (!main) {
      main = words[0];
      i = 1;
    }
    return { main, sub: words.slice(i).join(' ') };
  }

  companyTier(): { main: string; sub: string } {
    const name = String(this.activeCompanyDisplayName || this.userProfile?.companyName || this.userProfile?.companyId || '');
    return this.tierParts(name, 18, 16);
  }

  focusSearchInput(): void {
    this.searchInput.nativeElement.focus();
  }

  // ── The menu list: our own markup (the PrimeNG PanelMenu was dropped: its boxes, headings and focus rules fought every look) ──
  // Keyboard: Down/Up walk the visible rows, Home/End jump, Right opens a group, Left closes it (or goes to its group), Esc goes
  // back to the search box. Enter / Space press the focused row (they are real buttons). Rows inside a closed group are inert.
  private searchTermLower = '';
  private labelPartsCache = new Map<string, Array<{ text: string; hit: boolean }>>();

  /** The label split into plain and matched pieces while searching (cached: same input, same array). */
  labelParts(label: string | undefined): Array<{ text: string; hit: boolean }> {
    const text = label ?? '';
    const q = this.searching ? this.searchTermLower : '';
    const key = q + '|' + text;
    const cached = this.labelPartsCache.get(key);
    if (cached) return cached;
    if (this.labelPartsCache.size > 400) this.labelPartsCache.clear();
    const parts: Array<{ text: string; hit: boolean }> = [];
    if (!q) {
      parts.push({ text, hit: false });
    } else {
      const lower = text.toLowerCase();
      let i = 0;
      while (i < text.length) {
        const j = lower.indexOf(q, i);
        if (j < 0) {
          parts.push({ text: text.slice(i), hit: false });
          break;
        }
        if (j > i) parts.push({ text: text.slice(i, j), hit: false });
        parts.push({ text: text.slice(j, j + q.length), hit: true });
        i = j + q.length;
      }
      if (!parts.length) parts.push({ text, hit: false });
    }
    this.labelPartsCache.set(key, parts);
    return parts;
  }

  trackNav = (_: number, item: RoleMenuItem): string => (item.isHeading ? 'h:' + item.label : item.link ?? item.label ?? '');

  isNavActive(item: RoleMenuItem): boolean {
    return !!this.activeLink && item.link === this.activeLink;
  }

  onNavRowClick(item: RoleMenuItem): void {
    if (item.items?.length) {
      this.toggleNavGroup(item);
      return;
    }
    if (item.link) this.navigateMenuItem(item.link);
  }

  // One group open at a time (outside a search), like an accordion.
  private toggleNavGroup(group: RoleMenuItem): void {
    const open = !group.expanded;
    if (!this.searching) {
      for (const other of this.accessibleMenuItems ?? []) {
        if (other !== group && other.items?.length) other.expanded = false;
      }
    }
    group.expanded = open;
  }

  private navRows(): HTMLElement[] {
    const root = this.navElRef?.nativeElement;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>('.tms-nav-row')).filter((el) => !el.closest('[inert]'));
  }

  // Down arrow in the search box: into the list.
  focusFirstMenuItem(event: Event): void {
    const first = this.navRows()[0];
    if (first) {
      event.preventDefault();
      first.focus();
    }
  }

  onNavKeydown(event: KeyboardEvent): void {
    const rows = this.navRows();
    const i = rows.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    const row = rows[i];
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        rows[Math.min(i + 1, rows.length - 1)].focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (i === 0) this.searchInput?.nativeElement.focus();
        else rows[i - 1].focus();
        break;
      case 'Home':
        event.preventDefault();
        rows[0].focus();
        break;
      case 'End':
        event.preventDefault();
        rows[rows.length - 1].focus();
        break;
      case 'ArrowRight':
        if (row.getAttribute('aria-expanded') === 'false') {
          event.preventDefault();
          row.click();
        }
        break;
      case 'ArrowLeft':
        if (row.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          row.click();
        } else if (row.classList.contains('tms-nav-sub')) {
          for (let j = i - 1; j >= 0; j--) {
            if (!rows[j].classList.contains('tms-nav-sub')) {
              event.preventDefault();
              rows[j].focus();
              break;
            }
          }
        }
        break;
      case 'Escape':
        if (this.searching) {
          event.preventDefault();
          this.searchInput?.nativeElement.focus();
        }
        break;
    }
  }

  // Pinned bar (Settings, Support, Sync Issues): a group opens as a pop-up list above the bar, a page opens directly.
  pinnedPopupItems: MenuItem[] = [];

  openPinned(event: Event, item: RoleMenuItem): void {
    if (item.items?.length) {
      this.pinnedPopupItems = item.items.map((c) => ({
        label: c.label,
        icon: c.icon,
        command: () => {
          if (c.link) this.navigateMenuItem(c.link);
        },
      }));
      this.pinnedMenu?.toggle(event);
    } else if (item.link) {
      this.navigateMenuItem(item.link);
    }
  }

  copyCompanyId(): void {
    const id = this.userProfile?.companyId;
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => {
      // Said in place (the chip turns into "Copied"), no toast on top of the pop-up.
      this.idCopied = true;
      clearTimeout(this.idCopiedTimer);
      this.idCopiedTimer = setTimeout(() => (this.idCopied = false), 1600);
    }).catch(() => {
      this.toastMessageService.showErrorMessage('Copy failed', 'Could not copy to clipboard.');
    });
  }

  onHide() {
    this.searchInput.nativeElement.value = '';
    this.searchString = '';
    // AppComponent's own sweep only runs on router NavigationEnd — closing this drawer
    // WITHOUT navigating (its own X, clicking outside, or after opening the account menu
    // popup on top of it) was an uncovered path for the same orphaned-mask freeze bug (see
    // overlay-mask-sweep.ts). Covers it here regardless of how the drawer closes.
    scheduleOrphanedOverlayMaskSweep();
  }
  preloadImage() {
    const img = new Image();
    img.src = this.user?.imageUrl ?? '';
    img.onload = () => {
      this.imageIsValid = true;
    };
    img.onerror = () => {
      this.imageIsValid = false;
    };
  }


}
